import { Component, createRef } from 'preact';
import {
  BG_ATMOSPHERE,
  CONTENT_BG_DEFAULT,
  TEXT_BODY,
  TEXT_MUTED,
  TEXT_PRIMARY,
  VIGNETTE,
} from '../../lib/medievalTheme';
import { buildSorceryDeckMetrics } from '../../utils/sorcery/deckMetrics';
import {
  openingHandPlayability,
  thresholdReachByTurn,
} from '../../utils/sorcery/consistency';
import { AtlasCompositionCard } from './AtlasPanel';
import { ChartNoiseDefs } from './ChartNoise';
import {
  CardTypesCard,
  KeywordSignalsCard,
  TribesCard,
} from './BreakdownsPanel';
import { OpeningHandCard, ThresholdReachCard } from './ConsistencyPanel';
import { ManaCurveCard, MinionPowerCard } from './CurvesPanel';
import { RarityCollectionCard } from './RarityPanel';
import SummaryStrip from './SummaryStrip';
import { CloseButton } from '../ui/navButtons';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { t } from '../../localization/t';

function EmptyState() {
  return (
    <div className="flex items-center justify-center h-[60cqh]">
      <div
        className="max-w-[28cqw] text-center px-8 py-10 rounded-xl"
        style={{
          background: 'rgba(12,10,8,0.6)',
          border: '1px solid rgba(180,140,60,0.18)',
        }}
      >
        <h3 className="arena-heading text-xl font-bold mb-2" style={{ color: TEXT_PRIMARY }}>
          {t('Nothing to analyse yet')}
        </h3>
        <p className="text-sm leading-6" style={{ color: TEXT_BODY }}>
          {t('Add spells, sites, and an avatar to see threshold consistency, mana curve, tribal concentration, and opening-hand probabilities.')}
        </p>
      </div>
    </div>
  );
}

const COLUMN_WRAP = 'flex flex-col gap-3 min-w-0';

// Dedicated page surface for the statistics view. Same three-layer recipe
// as the starter-deck picker: BG_ATMOSPHERE's warm radial glows for depth,
// a stone/chisel/scratch texture wash overlay-blended against a 60% dark
// base so those glows still read through it, then the vignette. Deliberately
// NOT the deck-builder wall art: the sections here are frameless, so a
// photographic backdrop reads straight through the charts and numbers.
const TEXTURE_WASH = {
  background: `${CONTENT_BG_DEFAULT}, rgba(12, 10, 8, 0.6)`,
  backgroundBlendMode: 'overlay, normal',
};

// Full-page deck statistics view. Not a framed modal. It takes over the
// whole canvas on its own themed surface, dismissed via the X or Escape.
// Sections enter with a short stagger (`--stagger` drives the CSS delay,
// see vh-stats-* in app.css); exit is one fast page fade via `is-closing`.
class DeckStatisticsOverlay extends Component {
  constructor(props) {
    super(props);
    this.consistencyRef = createRef();
    this.state = { closing: false };
  }

  componentDidMount() {
    this._escDismissId = pushDismiss(() => this.requestClose());
  }

  componentWillUnmount() {
    removeDismiss(this._escDismissId);
    if (this._closeTimer) clearTimeout(this._closeTimer);
  }

  // Play the exit animation, then unmount via the parent's onClose one
  // exit-duration later.
  requestClose = () => {
    if (this.state.closing) return;
    this.setState({ closing: true });
    this._closeTimer = setTimeout(() => this.props.onClose?.(), 160);
  };

  handleJumpToConsistency = () => {
    this.consistencyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  render() {
    const { chosenCards, deckName } = this.props;
    const closingClass = this.state.closing ? ' is-closing' : '';

    const metrics = buildSorceryDeckMetrics(chosenCards);
    const reach = thresholdReachByTurn(chosenCards);
    const headline = openingHandPlayability(chosenCards);
    const consistency = { reach, headline };

    const isEmpty = metrics.totals.spellbookSize === 0 && metrics.totals.atlasSize === 0;
    const avatarCard = metrics.avatar;

    return (
      <div
        className={`vh-stats-page${closingClass} fixed inset-0 z-[60] overflow-hidden select-none`}
        style={{ background: BG_ATMOSPHERE }}
      >
        <div className="absolute inset-0 pointer-events-none" style={TEXTURE_WASH} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <ChartNoiseDefs />

        {/* The one fixed element: dismiss. Everything else scrolls as a page. */}
        <div className="absolute z-20" style={{ top: '1.25cqw', right: '1.5cqw' }}>
          <CloseButton onClick={this.requestClose} />
        </div>

        <div className="relative h-full overflow-y-auto px-10 pt-6 pb-10">
          <div className="vh-stats-sec pr-16" style={{ '--stagger': 0 }}>
            <h2 className="arena-heading text-2xl font-bold" style={{ color: TEXT_PRIMARY }}>
              {t('Deck Statistics')}
            </h2>
            <div className="mt-1 text-xs" style={{ color: TEXT_BODY }}>
              <span style={{ color: TEXT_PRIMARY }}>{deckName || t('Untitled deck')}</span>
              {avatarCard ? (
                <>
                  <span className="mx-1.5" style={{ color: TEXT_MUTED }}>·</span>
                  <span>{t('Avatar: {name}', { name: avatarCard.name })}</span>
                </>
              ) : null}
            </div>
          </div>

          {isEmpty ? (
            <div className="vh-stats-sec" style={{ '--stagger': 1 }}>
              <EmptyState />
            </div>
          ) : (
            <>
              <div className="vh-stats-sec mt-5" style={{ '--stagger': 1 }}>
                <SummaryStrip
                  metrics={metrics}
                  consistency={consistency}
                  onJumpToConsistency={this.handleJumpToConsistency}
                />
              </div>

              <div className="mt-6 flex flex-col gap-4">
                {/* Hero row. Opening hand as the full-width showpiece. */}
                <div className="vh-stats-sec" style={{ '--stagger': 2 }} ref={this.consistencyRef}>
                  <OpeningHandCard chosenCards={chosenCards} />
                </div>

                <div className="flex flex-col lg:flex-row gap-4 items-stretch">
                  {/* Wide column. Charts that need horizontal room. */}
                  <div className={`vh-stats-sec ${COLUMN_WRAP} lg:flex-[2]`} style={{ '--stagger': 3 }}>
                    <ThresholdReachCard reach={reach} />
                    <ManaCurveCard metrics={metrics} />
                    <MinionPowerCard metrics={metrics} />
                  </div>

                  {/* Narrow column. Compact breakdowns + atlas + rarity. */}
                  <div className={`vh-stats-sec ${COLUMN_WRAP} lg:flex-[1]`} style={{ '--stagger': 4 }}>
                    <CardTypesCard metrics={metrics} />
                    <TribesCard metrics={metrics} />
                    <KeywordSignalsCard metrics={metrics} />
                    <AtlasCompositionCard metrics={metrics} />
                    <RarityCollectionCard metrics={metrics} />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default DeckStatisticsOverlay;
